import React, { createContext, useContext, useState, useCallback, useEffect } from 'react'
import { CAMPAIGN } from '../config/campaign'

export interface TasteReservation {
  reservation_id: string
  amount: number
  expires_at: string
}

export interface InventoryLevel {
  total: number
  sold: number
  remaining: number
}

export interface ShippingAddress {
  street: string
  apt: string
  city: string
  state: string
  zip: string
}

export interface PackSelection {
  size: number
  price: number
  label: string
}

export interface TasteQuote {
  subtotal: number
  shippingCost: number
  taxAmount: number
  total: number
  zone: number
  shippingEstimated: boolean
}

interface TasteContextType {
  // Pack selection
  selectedPack: PackSelection | null
  setSelectedPack: (pack: PackSelection | null) => void

  // Caviar add-on
  hasCaviar: boolean
  setHasCaviar: (has: boolean) => void
  caviarPrice: number

  // Live shipping + tax quote
  quote: TasteQuote | null
  quoteLoading: boolean
  fetchQuote: () => void

  // Total
  totalAmount: number

  // Customer info
  customerEmail: string
  setCustomerEmail: (email: string) => void
  customerName: string
  setCustomerName: (name: string) => void

  // Shipping address
  shippingAddress: ShippingAddress
  setShippingAddress: (address: ShippingAddress) => void

  // Reservation
  reservation: TasteReservation | null
  createReservation: (amount: number) => Promise<void>

  // Inventory — single global pool of tails shared across all pack sizes
  inventory: Record<number, InventoryLevel> | null
  tailsRemaining: number | null
  inventoryLoaded: boolean
  campaignSoldOut: boolean
  campaignClosed: boolean
  isPackSoldOut: (size: number) => boolean

  // State
  loading: boolean
  error: string | null
  clearError: () => void
}

const TasteContext = createContext<TasteContextType | null>(null)

export const useTaste = () => {
  const context = useContext(TasteContext)
  if (!context) {
    throw new Error('useTaste must be used within a TasteProvider')
  }
  return context
}

// Caviar pricing by pack size
const CAVIAR_PRICES: Record<number, number> = {
  2: 50.00,
  4: 50.00,
  6: 45.00,
  8: 40.00,
}

export const TasteProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [selectedPack, setSelectedPack] = useState<PackSelection | null>({ size: 6, price: 135.00, label: '6 Tails' })
  const [hasCaviar, setHasCaviar] = useState(false)
  const [customerEmail, setCustomerEmail] = useState('')
  const [customerName, setCustomerName] = useState('')
  const [shippingAddress, setShippingAddress] = useState<ShippingAddress>({
    street: '',
    apt: '',
    city: '',
    state: '',
    zip: '',
  })
  const [reservation, setReservation] = useState<TasteReservation | null>(null)
  const [quote, setQuote] = useState<TasteQuote | null>(null)
  const [quoteLoading, setQuoteLoading] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Inventory — polled from the get-taste-inventory function. Single global
  // pool of tails (pieces); a pack size is unavailable once the pool can no
  // longer fulfill it, and the whole campaign is sold out at 0 remaining.
  const [inventory, setInventory] = useState<Record<number, InventoryLevel> | null>(null)
  const [tailsRemaining, setTailsRemaining] = useState<number | null>(null)
  const [inventoryLoaded, setInventoryLoaded] = useState(false)

  useEffect(() => {
    let cancelled = false
    const fetchInventory = async () => {
      try {
        const res = await fetch('/.netlify/functions/get-taste-inventory')
        if (res.ok) {
          const data = await res.json()
          if (cancelled) return
          setInventory(data.inventory ?? null)
          setTailsRemaining(typeof data.tailsRemaining === 'number' ? data.tailsRemaining : null)
        }
      } catch {
        // Silently fail — inventory display degrades gracefully
      } finally {
        if (!cancelled) setInventoryLoaded(true)
      }
    }
    fetchInventory()
    const interval = setInterval(fetchInventory, 60000) // Refresh every 60s
    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [])

  // A pack size is sold out when fewer than `size` tails remain in the pool.
  const isPackSoldOut = useCallback((size: number): boolean => {
    if (tailsRemaining === null) return false // unknown — don't block optimistically
    return tailsRemaining < size
  }, [tailsRemaining])

  // The whole campaign is sold out once the pool can't fulfill the smallest pack.
  const campaignSoldOut = tailsRemaining !== null && tailsRemaining < 2

  // The campaign is closed once the order deadline has passed (Sept 1, 2026
  // 11:59 PM PT). Distinct from sold-out — the page shows a "closed" state
  // with a waitlist instead of the sold-out card.
  const campaignClosed = Date.now() > CAMPAIGN.orderDeadline.getTime()

  // If the currently selected pack can no longer be fulfilled, drop it so the
  // checkout total/flow can't proceed against unavailable inventory.
  useEffect(() => {
    if (selectedPack && isPackSoldOut(selectedPack.size)) {
      setSelectedPack(null)
    }
  }, [selectedPack, isPackSoldOut])

  // Calculate caviar price based on selected pack
  const caviarPrice = selectedPack ? (CAVIAR_PRICES[selectedPack.size] || 50.00) : 0

  // Calculate total amount — prefer the server quote's total (which includes
  // shipping + tax) when one exists; otherwise fall back to the pack+caviar
  // subtotal so the UI works before a quote has been fetched.
  const productSubtotal = (selectedPack?.price || 0) + (hasCaviar ? caviarPrice : 0)
  const totalAmount = quote ? quote.total : productSubtotal

  // Fetch a live shipping + tax quote for the current pack, caviar, and
  // destination. Only fires when a state and a complete 5-digit ZIP are present.
  const fetchQuote = useCallback(() => {
    const size = selectedPack?.size
    const { state, zip } = shippingAddress
    if (!size || !state || !/^\d{5}$/.test(zip)) {
      setQuote(null)
      return
    }
    let cancelled = false
    setQuoteLoading(true)
    fetch('/.netlify/functions/get-taste-quote', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ packSize: size, hasCaviar, state, zip }),
    })
      .then(async (res) => {
        if (!res.ok) throw new Error('Quote request failed')
        const data = await res.json()
        if (cancelled) return
        setQuote({
          subtotal: data.subtotal,
          shippingCost: data.shippingCost,
          taxAmount: data.taxAmount,
          total: data.total,
          zone: data.zone,
          shippingEstimated: !!data.shippingEstimated,
        })
      })
      .catch(() => {
        if (!cancelled) setQuote(null)
      })
      .finally(() => {
        if (!cancelled) setQuoteLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [selectedPack?.size, hasCaviar, shippingAddress])

  // Create a reservation for the order amount
  const createReservation = useCallback(async (amt: number) => {
    setLoading(true)
    setError(null)

    try {
      const response = await fetch('/.netlify/functions/create-taste-reservation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ amount: amt }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to create reservation')
      }

      const data = await response.json()
      setReservation(data)
    } catch (err: any) {
      console.error('Error creating reservation:', err)
      setError(err.message || 'Failed to create reservation')
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  // Clear error state
  const clearError = useCallback(() => {
    setError(null)
  }, [])

  return (
    <TasteContext.Provider
      value={{
        selectedPack,
        setSelectedPack,
        hasCaviar,
        setHasCaviar,
        caviarPrice,
        quote,
        quoteLoading,
        fetchQuote,
        totalAmount,
        customerEmail,
        setCustomerEmail,
        customerName,
        setCustomerName,
        shippingAddress,
        setShippingAddress,
        reservation,
        createReservation,
        inventory,
        tailsRemaining,
        inventoryLoaded,
        campaignSoldOut,
        campaignClosed,
        isPackSoldOut,
        loading,
        error,
        clearError,
      }}
    >
      {children}
    </TasteContext.Provider>
  )
}

export default TasteProvider
